import { Chat } from "./Chat";
import { DateTime } from "luxon";
import {
  TicketAttachment,
  TicketPriority,
  TicketRepeatFrequency,
  TicketStatus,
} from "./Ticket";

export type BoardItemType = "contact" | "card";

export type BoardItemHistory = {
  createdBy?: string;
  currentStageId: string;
  previousStageId?: string;
  addedAt: DateTime;
  removedAt?: DateTime;
};

export type CustomField = {
  name: string;
  value?: any;
  type: "text" | "number" | "date";
};

export type BoardItemComment = {
  id: number;
  userId: string;
  comment: string;
  meta?: {
    refId?: string;
    mentionedUsers?: string[];
    [key: string]: any;
  };
  createdAt: DateTime;
  updatedAt: DateTime;
};

export type BoardItemTransaction = {
  id: string;
  receipt?: string;
  amount: number;
  date: string;
  paymentMethod: string;
  currency?: string;
  status: "completed" | "in-complete" | "pending";
};

export type BoardItemCard = {
  id: number;
  itemId?: string;
  uuid: string;
  parentId?: number;
  boardId?: number;
  columnId?: number;
  name: string;
  subscriberId?: number;
  dueDate?: DateTime;
  type: string;
  priority?: TicketPriority;
  description?: string;
  status?: TicketStatus;
  repeatFrequency?: TicketRepeatFrequency;
  repeatConfig?: number[];
  transactions?: BoardItemTransaction[];
  livecardId?: number;
  assigneeId: string;
  createdBy?: string;
  opportunity: number;
  createdAt: DateTime;
  updatedAt: DateTime;
  openedAt?: DateTime;
  closedAt?: DateTime;
  closedLostReason?: string;
  archivedAt?: DateTime;
  histories?: BoardItemHistory[];
  comments?: BoardItemComment[];
  attachments?: TicketAttachment[];
  labels?: any[];
  customFields?: CustomField[];
  subscriber?: Chat;
  newMessageCount?: number;
  meta?: any;
  subitems?: BoardItemCard[];
};

export type BoardItem = {
  id: string;
  type: BoardItemType;
} & (
  | BoardItemCard
  | {
      type: "contact";
    }
);

export type BoardColumn = {
  id: number;
  uuid: string;
  name: string;
  type: BoardColummType;
  columnSorting: number;
  probability: number;
  forecastCategory: ForecastCategory;
  noOfItems: number;
  sumOfOpportunity: number;
  description?: string;
  createdAt: DateTime;
  updatedAt: DateTime;
  items?: BoardItemCard[];
};

export type Board = {
  id: number;
  uuid: string;
  name: string;
  itemTypes: { id: string; label: string }[];
  paymentMethods: { id: string; label: string }[];
  columns?: BoardColumn[];
  createdAt: DateTime;
  updatedAt: DateTime;
  deletedAt?: DateTime;
  isDefault: boolean;
};

export enum BoardColummType {
  Open = "open",
  InProgress = "in-progress",
  ClosedWon = "closed-won",
  ClosedLost = "closed-lost",
  Completed = "completed",
}

export const boardColumnTypeMapByMode: Record<
  "ticket" | "pipeline",
  Record<string, string>
> = {
  ticket: {
    [BoardColummType.Open]: "Open",
    [BoardColummType.InProgress]: "In Progress",
    [BoardColummType.Completed]: "Completed",
  },
  pipeline: {
    [BoardColummType.Open]: "Open",
    [BoardColummType.ClosedWon]: "Closed Won",
    [BoardColummType.ClosedLost]: "Closed Lost",
  },
};

export enum ForecastCategory {
  Omitted = "omitted",
  Pipeline = "pipeline",
  BestCase = "best-case",
  Commit = "commit",
  Closed = "closed",
}

export const forecastCategoryProbabilityMap: Record<ForecastCategory, number> =
  {
    [ForecastCategory.Omitted]: 0,
    [ForecastCategory.Pipeline]: 25,
    [ForecastCategory.BestCase]: 70,
    [ForecastCategory.Commit]: 90,
    [ForecastCategory.Closed]: 0,
  };

export const forecastCategoryMap: Record<ForecastCategory, string> = {
  [ForecastCategory.Omitted]: "Omitted",
  [ForecastCategory.Pipeline]: "Pipeline",
  [ForecastCategory.BestCase]: "Best Case",
  [ForecastCategory.Commit]: "Commit",
  [ForecastCategory.Closed]: "Closed",
};

export enum DueDateFilter {
  Overdue = -1,
  Today = 0,
  ThreeDays = 3,
  EightDays = 8,
}
